/* eslint-disable react-hooks/exhaustive-deps */
"use client"

import React, { useRef, useMemo, useCallback } from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'
import { Box, makeStyles, Paper, Typography, FormControl, Grid } from "@material-ui/core"
import MaterialSelect from "../../components/MaterialSelect"

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  paper: {
    padding: theme.spacing(2),
    height: 400,
    position: 'relative',
  },
  title: {
    fontSize: "0.9rem",
    color: "#1976d2",
  },
  chartContainer: {
    height: "calc(100% - 60px)",
  },
  controlsContainer: {
    alignItems: 'center',
  },
  control: {
    minWidth: 100,
    height: 30,
    fontSize: '0.75rem',
    '& .MuiSelect-select': {
      padding: '4px 8px',
    },
  },
}));

const generateSampleData = (period) => {
  const baseData = {
    week: [
      { date: '2024-01-01', value: 10 },
      { date: '2024-01-08', value: 25 },
      { date: '2024-01-15', value: 45 },
      { date: '2024-01-22', value: 70 },
      { date: '2024-01-29', value: 85 },
      { date: '2024-02-05', value: 95 },
      { date: '2024-02-12', value: 100 },
    ],
    month: [
      { date: 'Ene', value: 15 },
      { date: 'Feb', value: 35 },
      { date: 'Mar', value: 60 },
      { date: 'Abr', value: 80 },
      { date: 'May', value: 90 },
      { date: 'Jun', value: 95 },
    ],
    year: [
      { date: '2020', value: 20 },
      { date: '2021', value: 45 },
      { date: '2022', value: 75 },
      { date: '2023', value: 90 },
      { date: '2024', value: 100 },
    ]
  };

  return baseData[period] || baseData.semanas;
};

const CHART_VALUES = {
  year: '0',
  month: '1',
  week: '2'
}

const CHART_TYPES = {
  BARRA: 'barra',
  LINEA: 'linea',
  CURVA_S: 'curvaS'
}

const MultiTypeChart = ({
  title = "Gráfico de Progreso",
  selectedCoin,
  selectedRow,
  setSelectedRow,
  data: externalData,
  handleRowDoubleClick,
  selectedPeriod,
  setSelectedPeriod,
  chartType,
  setChartType,
  options,
  mainColor,
}) => {
  const classes = useStyles();
  const chartRef = useRef();
  const clickCountRef = useRef(0);
  const timeoutRef = useRef(null);
  const lastElementRef = useRef(null);

  const sampleData = useMemo(() => {
    const periodKey = selectedPeriod === CHART_VALUES.year 
                        ? 'year' 
                        : selectedPeriod === CHART_VALUES.month 
                            ? 'month' 
                            : 'week';
    return generateSampleData(periodKey);
  }, [selectedPeriod]);

  const data = externalData || sampleData;

  const chartData = useMemo(() => {
    const labels = data.map(item => item.date);
    const values = data.map(item => item.value);

    const isLineChart = chartType === CHART_TYPES.LINEA || chartType === CHART_TYPES.CURVA_S;
    const isCurvaS = chartType === CHART_TYPES.CURVA_S;
    
    return {
      labels,
      datasets: [
        {
          label: '',
          data: values,
          backgroundColor: chartType === CHART_TYPES.BARRA ? mainColor : 'transparent',
          borderColor: isLineChart ? mainColor : undefined,
          borderWidth: isLineChart ? 2 : 0,
          borderDash: chartType === CHART_TYPES.LINEA ? [] : undefined,
          pointBackgroundColor: isLineChart ? mainColor : undefined,
          pointBorderColor: isLineChart ? mainColor : undefined,
          pointBorderWidth: isLineChart ? 2 : undefined,
          pointRadius: isLineChart ? 4 : undefined,
          tension: isCurvaS ? 0.4 : 0,
          fill: isCurvaS,
        },
      ],
    };
  }, [data, chartType, mainColor]);

  const handleChartClick = useCallback((event, elements) => {
    if (elements.length === 0) return;

    const element = elements[0];
    const index = element.index;

    clickCountRef.current++;

    if (clickCountRef.current === 1) {
      timeoutRef.current = setTimeout(() => {
        if (clickCountRef.current === 1 && setSelectedRow) {
        }
        clickCountRef.current = 0;
        lastElementRef.current = null;
      }, 300);
    } else if (clickCountRef.current === 2) {
      clearTimeout(timeoutRef.current);
      
      if (lastElementRef.current === index && handleRowDoubleClick) {
        const clickedData = {
          index: index,
          label: data[index]?.date || null,
          value: data[index]?.value || null,
          datasetIndex: element.datasetIndex,
          element: element,
          period: selectedPeriod,
          chartType: chartType
        };
        handleRowDoubleClick(clickedData);
      }
      
      clickCountRef.current = 0;
      lastElementRef.current = null;
    }

    lastElementRef.current = index;
  }, [data, selectedPeriod, chartType, handleRowDoubleClick, setSelectedRow]);

  const handlePeriodChange = useCallback((event) => {
    const newPeriod = event.target.value;
    setSelectedPeriod(newPeriod);
  }, [setSelectedPeriod]);

  const handleChartTypeChange = useCallback((event) => {
    if (setChartType) {
      setChartType(event.target.value);
    }
  }, [setChartType]);

  const defaultOptions = useMemo(() => {
    const maxValue = Math.max(...data.map(item => item.value));
    const yMax = Math.ceil(maxValue * 1.05);

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `value: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: yMax,
          ticks: {
            callback: function(value) {
              return value;
            }
          },
          title: {
            display: false
          }
        },
        x: {
          title: {
            display: false
          }
        }
      },
      onClick: handleChartClick,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      elements: {
        bar: {
          hoverBackgroundColor: mainColor,
          borderRadius: 4,
        },
        line: {
          hoverBackgroundColor: mainColor,
        },
        point: {
          hoverRadius: 6,
        }
      }
    };
  }, [data, handleChartClick, mainColor]);

  const mergedOptions = useMemo(() => ({
    ...defaultOptions,
    ...options,
    onClick: handleChartClick,
  }), [defaultOptions, options, handleChartClick]);

  const renderChart = () => {
    const chartProps = {
      data: chartData,
      options: mergedOptions,
      ref: chartRef,
    };

    switch (chartType) {
      case CHART_TYPES.LINEA:
      case CHART_TYPES.CURVA_S:
        return <Line {...chartProps} />;
      case CHART_TYPES.BARRA:
      default:
        return <Bar {...chartProps} />;
    }
  };

  return (
    <Paper className={classes.paper}>
      <Grid container direction="column">
        <Grid item>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
        </Grid>
        <Grid item>
          <div className={classes.controlsContainer}>
            <FormControl variant="outlined" size="small" className={classes.control}>
              <MaterialSelect
                label=""
                value={chartType}
                onChange={handleChartTypeChange}
                width="8em"
              >
                <option value={CHART_TYPES.BARRA}>Barra</option>
                <option value={CHART_TYPES.LINEA}>Línea</option>
                <option value={CHART_TYPES.CURVA_S}>Curva S</option>
              </MaterialSelect>
            </FormControl>

            <FormControl variant="outlined" size="small" className={classes.control}>
              <MaterialSelect
                label=""
                value={selectedPeriod}
                onChange={handlePeriodChange}
                width="8em"
              >
                <option value={CHART_VALUES.year}>Año</option>
                <option value={CHART_VALUES.month}>Mes</option>
                <option value={CHART_VALUES.week}>Semana</option>
              </MaterialSelect>
            </FormControl>
          </div>
        </Grid>
      </Grid>

      <Box className={classes.chartContainer}>
        {renderChart()}
      </Box>
    </Paper>
  );
};

export default MultiTypeChart;